/**
 * Per-row ride-height (foiling) state for the CALIBRATION pages.
 *
 * H0 is the boat on the foils; every other state is not. That single rule is
 * what `FOILING` / `NON-FOILING` mean on these pages - deliberately the
 * pipeline's definition and not, say, a hull-height threshold, so a row's state
 * means the same thing here as the FOILING_STATE tag does on a maneuver and as
 * `Foiling_state > 0` does in the server's displacement query.
 *
 * The state is READ, not invented: both `1_normalization_*.py` (parquet) and
 * `server_stream/controllers/processor.js` (live) already write a per-row
 * numeric `Foiling_state` of 0/1/2. Only when that column is absent - an older
 * parquet, a projection that dropped it - is it re-derived here from the same
 * thresholds those two and `u.getFoilingState` share:
 *
 *   BSP > 15 kt, heel < 8°  -> H0   (flying)
 *   BSP > 15 kt, heel > 8°  -> H1
 *   BSP < 15 kt, heel > 5°  -> H1
 *   BSP < 15 kt, heel < 5°  -> H2   (displacement)
 */

const KPH_PER_KNOT = 1.852;

/** BSP split between the "fast" and "slow" heel thresholds (knots). */
export const FOILING_BSP_THRESHOLD_KTS = 15;

/** Heel gate above `FOILING_BSP_THRESHOLD_KTS`: below this and the boat is flying. */
export const FOILING_HEEL_FAST_THRESHOLD_DEG = 8;

/** Heel gate below `FOILING_BSP_THRESHOLD_KTS`: below this and the boat is fully displacement. */
export const FOILING_HEEL_SLOW_THRESHOLD_DEG = 5;

export type RideHeightState = 'H0' | 'H1' | 'H2';

/** The calibration-page selector. `ALL` is the no-op default: every row passes. */
export type FoilingFilter = 'ALL' | 'FOILING' | 'NON_FOILING';

/** Dropdown labels, in menu order. `foilingFilterFromLabel` maps them back. */
export const FOILING_FILTER_OPTIONS = ['ALL', 'FOILING', 'NON-FOILING'] as const;

export function foilingFilterLabel(filter: FoilingFilter): string {
  return filter === 'NON_FOILING' ? 'NON-FOILING' : filter;
}

/**
 * Parse a dropdown label (or a persisted value) back to a filter; unknown text is `ALL`.
 *
 * `DISPLACEMENT` is accepted as a synonym for `NON_FOILING` so shared links from the fleet page's
 * old hull-height toggle still restore the side the sender was looking at.
 */
export function foilingFilterFromLabel(value: unknown): FoilingFilter {
  const v = String(value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (v === 'FOILING') {return 'FOILING';}
  if (v === 'NON_FOILING' || v === 'DISPLACEMENT') {return 'NON_FOILING';}
  return 'ALL';
}

/** Column names the stored (already-derived) foiling state can arrive under. */
const STORED_STATE_FIELDS = [
  'Foiling_state',
  'FOILING_STATE',
  'foiling_state',
  'FoilingState',
  'foilingState',
] as const;

type Row = Record<string, unknown>;

function numericField(row: Row, keys: readonly string[]): number | null {
  for (const key of keys) {
    const raw = row[key];
    if (raw === undefined || raw === null || raw === '') {continue;}
    const n = Number(raw);
    if (Number.isFinite(n)) {return n;}
  }
  return null;
}

/**
 * A stored foiling state, as either `'H0'|'H1'|'H2'` or the live processor's
 * numeric 0/1/2. Anything else (including the pipeline's `'NA'`) is `null`, so
 * the caller falls through to the BSP/heel derivation.
 */
function storedRideHeightState(row: Row): RideHeightState | null {
  for (const key of STORED_STATE_FIELDS) {
    const raw = row[key];
    if (raw === undefined || raw === null || raw === '') {continue;}
    if (typeof raw === 'number' || (typeof raw === 'string' && /^[0-2](\.0+)?$/.test(raw.trim()))) {
      const n = Number(raw);
      if (n === 0) {return 'H0';}
      if (n === 1) {return 'H1';}
      if (n === 2) {return 'H2';}
      continue;
    }
    const str = String(raw).trim().toUpperCase();
    if (str === 'H0' || str === 'H1' || str === 'H2') {return str;}
  }
  return null;
}

/** BSP in knots from whichever speed column the row carries. */
function rowBspKts(row: Row): number | null {
  const kts = numericField(row, ['Bsp_kts', 'BSP_KTS', 'bsp_kts']);
  if (kts !== null) {return Math.abs(kts);}
  const kph = numericField(row, ['Bsp_kph', 'BSP_KPH', 'bsp_kph']);
  if (kph !== null) {return Math.abs(kph) / KPH_PER_KNOT;}
  return null;
}

/**
 * Normalised heel. `Heel_n_deg` is the tack-normalised channel the pipeline
 * reads; `Heel_deg` is the signed fallback, so its magnitude is used - the
 * thresholds are about how far the boat is over, not which way.
 */
function rowHeelDeg(row: Row): number | null {
  const heelN = numericField(row, ['Heel_n_deg', 'HEEL_N_DEG', 'heel_n_deg']);
  if (heelN !== null) {return heelN;}
  const heel = numericField(row, ['Heel_deg', 'HEEL_DEG', 'heel_deg']);
  if (heel !== null) {return Math.abs(heel);}
  return null;
}

/**
 * The row's ride-height state, or `null` when neither a stored state nor both
 * of BSP and heel are present. A null state is unknown, not "not foiling".
 */
export function rowRideHeightState(row: Row): RideHeightState | null {
  const stored = storedRideHeightState(row);
  if (stored !== null) {return stored;}

  const bspKts = rowBspKts(row);
  const heel = rowHeelDeg(row);
  if (bspKts === null || heel === null) {return null;}

  if (bspKts > FOILING_BSP_THRESHOLD_KTS) {
    return heel < FOILING_HEEL_FAST_THRESHOLD_DEG ? 'H0' : 'H1';
  }
  if (bspKts < FOILING_BSP_THRESHOLD_KTS) {
    return heel < FOILING_HEEL_SLOW_THRESHOLD_DEG ? 'H2' : 'H1';
  }
  // Exactly at the threshold: the Python falls through to its H1 default.
  return 'H1';
}

/**
 * Whether a row belongs in the selected population.
 *
 * `ALL` keeps everything, including rows whose state cannot be read. Once a
 * side is chosen, an unreadable state is excluded from BOTH - a row that cannot
 * be classified is not evidence for either population, and silently counting it
 * as non-foiling would inflate the displacement side with every row missing a
 * heel or speed sample.
 */
export function passesFoilingFilter(row: Row, filter: FoilingFilter): boolean {
  if (filter === 'ALL') {return true;}
  const state = rowRideHeightState(row);
  if (state === null) {return false;}
  return filter === 'FOILING' ? state === 'H0' : state !== 'H0';
}

/** `passesFoilingFilter` over a whole array; returns the input when nothing is filtered. */
export function filterRowsByFoilingState<T extends Row>(rows: T[], filter: FoilingFilter): T[] {
  if (filter === 'ALL') {return rows;}
  return rows.filter((row) => passesFoilingFilter(row, filter));
}

/** Channels a page must request for `rowRideHeightState` to work; the last three are the fallback. */
export const FOILING_STATE_CHANNELS = ['Foiling_state', 'Bsp_kts', 'Bsp_kph', 'Heel_n_deg'] as const;

/** Short note for the page, naming the rule so the split is not mistaken for a hull-height gate. */
export function foilingFilterDescription(filter: FoilingFilter): string {
  if (filter === 'FOILING') {return 'Foiling only (state H0)';}
  if (filter === 'NON_FOILING') {return 'Non-foiling only (states H1/H2)';}
  return 'All ride-height states';
}
